import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { EntityHistoryPanel } from "../components/EntityHistoryPanel";

type NcStatus = "Open" | "In_Progress" | "Awaiting_Verification" | "Closed";
type NcClassification = "Minor" | "Major" | "Observation" | "Opportunity";
type CapaStatus = "Open" | "In_Progress" | "Completed" | "Verified";

interface NcDetail {
  uuid: string;
  reference: string;
  title: string;
  description: string;
  classification: NcClassification;
  status: NcStatus;
  raisedAt: string;
  raisedBy: { uuid: string; fullName: string; email: string; erased: boolean } | null;
  relatedClause: {
    uuid: string;
    standardCode: string;
    versionYear: number;
    clauseReference: string;
    clauseTitle: string;
  } | null;
  closedAt: string | null;
  allowedTransitions: NcStatus[];
}

interface CapaSummary {
  uuid: string;
  description: string;
  status: CapaStatus;
  dueDate: string;
  completedAt: string | null;
  effectivenessVerifiedAt: string | null;
  effectivenessNotes: string | null;
  owner: { uuid: string; fullName: string; email: string; erased: boolean } | null;
  verifier: { uuid: string; fullName: string; email: string; erased: boolean } | null;
}

interface MemberSummary {
  uuid: string;
  fullName: string;
  email: string;
  status: "active" | "pending";
}

const NC_STATUS_TONE: Record<NcStatus, string> = {
  Open: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  In_Progress: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  Awaiting_Verification: "bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-200",
  Closed: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
};

const NC_STATUS_LABEL: Record<NcStatus, string> = {
  Open: "Open",
  In_Progress: "In progress",
  Awaiting_Verification: "Awaiting verification",
  Closed: "Closed",
};

const NC_TRANSITION_LABEL: Record<NcStatus, string> = {
  Open: "Re-open",
  In_Progress: "Mark in progress",
  Awaiting_Verification: "Submit for verification",
  Closed: "Close",
};

const CAPA_STATUS_TONE: Record<CapaStatus, string> = {
  Open: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  In_Progress: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  Completed: "bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-200",
  Verified: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
};

const CAPA_STATUS_LABEL: Record<CapaStatus, string> = {
  Open: "Open",
  In_Progress: "In progress",
  Completed: "Completed",
  Verified: "Verified",
};

const CAPA_TRANSITION_LABEL: Record<CapaStatus, string> = {
  Open: "Re-open",
  In_Progress: "Mark in progress",
  Completed: "Mark completed",
  Verified: "Verify effectiveness",
};

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function NonconformityDetail() {
  const { slug, uuid } = useParams<{ slug: string; uuid: string }>();
  const [nc, setNc] = useState<NcDetail | null>(null);
  const [capas, setCapas] = useState<CapaSummary[] | null>(null);
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [pendingNcTransition, setPendingNcTransition] = useState<NcStatus | null>(null);
  const [ncTransitionComment, setNcTransitionComment] = useState("");
  const [showCapaForm, setShowCapaForm] = useState(false);
  const [capaDescription, setCapaDescription] = useState("");
  const [capaOwnerUuid, setCapaOwnerUuid] = useState("");
  const [capaDueDate, setCapaDueDate] = useState("");
  const [pendingCapa, setPendingCapa] = useState<{ uuid: string; to: CapaStatus } | null>(null);
  const [capaNotes, setCapaNotes] = useState("");

  const refresh = useCallback(async () => {
    if (!uuid) return;
    try {
      const [ncRes, capaRes, memRes] = await Promise.all([
        api<{ nonconformity: NcDetail }>(`/nonconformities/${uuid}`),
        api<{ correctiveActions: CapaSummary[] }>(`/nonconformities/${uuid}/corrective-actions`),
        api<{ members: MemberSummary[] }>(`/memberships`),
      ]);
      setNc(ncRes.nonconformity);
      setCapas(capaRes.correctiveActions);
      setMembers(memRes.members.filter((m) => m.status === "active"));
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, [uuid]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (errors.length > 0 && !nc) {
    return (
      <div
        role="alert"
        className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
      >
        {errors[0]}
      </div>
    );
  }

  if (!nc || !capas) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  }

  const submitNcTransition = async (event: FormEvent) => {
    event.preventDefault();
    if (!pendingNcTransition) return;
    setBusy(true);
    setErrors([]);
    try {
      await api(`/nonconformities/${nc.uuid}/transition`, {
        method: "POST",
        body: { to: pendingNcTransition, comment: ncTransitionComment.trim() || null },
      });
      setPendingNcTransition(null);
      setNcTransitionComment("");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCapa = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setErrors([]);
    try {
      await api(`/nonconformities/${nc.uuid}/corrective-actions`, {
        method: "POST",
        body: { description: capaDescription, ownerUserUuid: capaOwnerUuid, dueDate: capaDueDate },
      });
      setShowCapaForm(false);
      setCapaDescription("");
      setCapaOwnerUuid("");
      setCapaDueDate("");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCapaTransition = async (event: FormEvent) => {
    event.preventDefault();
    if (!pendingCapa) return;
    setBusy(true);
    setErrors([]);
    try {
      await api(`/corrective-actions/${pendingCapa.uuid}/transition`, {
        method: "POST",
        body: {
          to: pendingCapa.to,
          effectivenessNotes: pendingCapa.to === "Verified" ? capaNotes.trim() || null : null,
        },
      });
      setPendingCapa(null);
      setCapaNotes("");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/o/${slug}/nonconformities`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← All nonconformities
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          <span className="mr-2 font-mono text-base text-slate-500 dark:text-slate-400">
            {nc.reference}
          </span>
          {nc.title}
        </h1>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Raised {formatDateUk(nc.raisedAt)}
          {nc.raisedBy
            ? ` by ${nc.raisedBy.erased ? "Anonymous (erased)" : nc.raisedBy.fullName}`
            : ""}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium uppercase tracking-wide ${NC_STATUS_TONE[nc.status]}`}
        >
          {NC_STATUS_LABEL[nc.status]}
        </span>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
          {nc.classification}
        </span>
        {nc.relatedClause ? (
          <Link
            to={`/o/${slug}/standards/${nc.relatedClause.standardCode}/${nc.relatedClause.versionYear}/${encodeURIComponent(nc.relatedClause.clauseReference)}`}
            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            {nc.relatedClause.standardCode.replace(/^ISO/, "ISO ")}:{nc.relatedClause.versionYear}{" "}
            cl. {nc.relatedClause.clauseReference}
          </Link>
        ) : null}
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul className="list-disc space-y-0.5 pl-4">
              {errors.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Description
        </h2>
        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-100">
          {nc.description}
        </p>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Workflow
        </h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {nc.allowedTransitions.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No further transitions available from this status with your role.
            </p>
          ) : (
            nc.allowedTransitions.map((to) => (
              <button
                key={to}
                type="button"
                onClick={() => setPendingNcTransition(to)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                {NC_TRANSITION_LABEL[to]}
              </button>
            ))
          )}
        </div>

        {pendingNcTransition ? (
          <form
            onSubmit={submitNcTransition}
            className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Transitioning from <strong>{NC_STATUS_LABEL[nc.status]}</strong> to{" "}
              <strong>{NC_STATUS_LABEL[pendingNcTransition]}</strong>.
            </p>
            <textarea
              value={ncTransitionComment}
              onChange={(e) => setNcTransitionComment(e.target.value)}
              placeholder="Optional comment"
              rows={2}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {busy ? "Working…" : "Confirm"}
              </button>
              <button
                type="button"
                onClick={() => setPendingNcTransition(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Corrective actions
          </h2>
          {nc.status !== "Closed" ? (
            <button
              type="button"
              onClick={() => setShowCapaForm((v) => !v)}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              {showCapaForm ? "Cancel" : "Add corrective action"}
            </button>
          ) : null}
        </div>

        {showCapaForm ? (
          <form
            onSubmit={submitCapa}
            className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <div>
              <label
                htmlFor="capa-desc"
                className="block text-xs font-medium text-slate-700 dark:text-slate-200"
              >
                What needs to happen?
              </label>
              <textarea
                id="capa-desc"
                rows={3}
                required
                value={capaDescription}
                onChange={(e) => setCapaDescription(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="capa-owner"
                  className="block text-xs font-medium text-slate-700 dark:text-slate-200"
                >
                  Owner
                </label>
                <select
                  id="capa-owner"
                  required
                  value={capaOwnerUuid}
                  onChange={(e) => setCapaOwnerUuid(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">Select an owner…</option>
                  {(members ?? []).map((m) => (
                    <option key={m.uuid} value={m.uuid}>
                      {m.fullName} ({m.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="capa-due"
                  className="block text-xs font-medium text-slate-700 dark:text-slate-200"
                >
                  Due date
                </label>
                <input
                  id="capa-due"
                  type="date"
                  required
                  value={capaDueDate}
                  onChange={(e) => setCapaDueDate(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save corrective action"}
            </button>
          </form>
        ) : null}

        {capas.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
            No corrective actions yet.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {capas.map((capa) => (
              <li
                key={capa.uuid}
                className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900 dark:text-slate-100">{capa.description}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Owner: {capa.owner ? capa.owner.fullName : "—"} · Due:{" "}
                      {formatDateUk(capa.dueDate)}
                      {capa.completedAt ? ` · Completed: ${formatDateUk(capa.completedAt)}` : ""}
                      {capa.effectivenessVerifiedAt
                        ? ` · Verified: ${formatDateUk(capa.effectivenessVerifiedAt)}${capa.verifier ? ` by ${capa.verifier.fullName}` : ""}`
                        : ""}
                    </p>
                    {capa.effectivenessNotes ? (
                      <p className="mt-1 text-xs italic text-slate-600 dark:text-slate-300">
                        Verification notes: {capa.effectivenessNotes}
                      </p>
                    ) : null}
                  </div>
                  <span
                    className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${CAPA_STATUS_TONE[capa.status]}`}
                  >
                    {CAPA_STATUS_LABEL[capa.status]}
                  </span>
                </div>
                <CapaActions
                  capaUuid={capa.uuid}
                  status={capa.status}
                  onTransition={(to) => setPendingCapa({ uuid: capa.uuid, to })}
                />
                {pendingCapa?.uuid === capa.uuid ? (
                  <form
                    onSubmit={submitCapaTransition}
                    className="mt-2 space-y-2 rounded border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Transitioning to <strong>{CAPA_STATUS_LABEL[pendingCapa.to]}</strong>.
                      {pendingCapa.to === "Verified" ? (
                        <span className="ml-1">
                          Reminder: the verifier must not be the action owner — the server will
                          reject if you are the owner of this action.
                        </span>
                      ) : null}
                    </p>
                    {pendingCapa.to === "Verified" ? (
                      <textarea
                        value={capaNotes}
                        onChange={(e) => setCapaNotes(e.target.value)}
                        placeholder="Effectiveness notes (optional)"
                        rows={2}
                        className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      />
                    ) : null}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={busy}
                        className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                      >
                        {busy ? "Working…" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingCapa(null)}
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <EntityHistoryPanel entityType="nonconformities" entityUuid={nc.uuid} />
    </div>
  );
}

function CapaActions({
  capaUuid: _capaUuid,
  status,
  onTransition,
}: {
  capaUuid: string;
  status: CapaStatus;
  onTransition: (to: CapaStatus) => void;
}) {
  // The allowed transitions are sourced server-side via getCorrectiveAction;
  // here we mirror them statically since they are role-stable per status.
  const transitions: CapaStatus[] =
    status === "Open"
      ? ["In_Progress"]
      : status === "In_Progress"
        ? ["Completed", "Open"]
        : status === "Completed"
          ? ["Verified", "In_Progress"]
          : [];
  if (transitions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {transitions.map((to) => (
        <button
          key={to}
          type="button"
          onClick={() => onTransition(to)}
          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:border-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        >
          {CAPA_TRANSITION_LABEL[to]}
        </button>
      ))}
    </div>
  );
}
